import type { ReactNode } from "react";
import AnimatedSection from "./AnimatedSection";

/**
 * Telemetry section header — the system-readout index that breaks the
 * centered-stack monotony below the fold:
 *
 *   01 // SERVICES          ← mono kicker, hard left (or right)
 *   Four trades, one workshop   ← display type at real scale
 *
 * A huge dim index numeral watermarks the corner behind the headline.
 * Sections alternate `align` left/right so the eye zigzags down the
 * page instead of riding a center rail. `center` remains for the few
 * sections whose content wants symmetry (the contact form).
 */
const SectionHeader = ({
  index,
  code,
  title,
  blurb,
  align = "left",
  className = "mb-16",
}: {
  index: string;
  code: string;
  title: ReactNode;
  blurb?: ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}) => {
  const alignCls =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const blurbCls =
    align === "right" ? "ml-auto" : align === "center" ? "mx-auto" : "";
  const watermarkCls = align === "right" ? "right-0" : align === "center" ? "left-1/2 -translate-x-1/2" : "left-0";

  return (
    <AnimatedSection className={`relative ${alignCls} ${className}`}>
      {/* watermark index — composition anchor, not content */}
      <span
        aria-hidden
        className={`pointer-events-none absolute -top-12 ${watermarkCls} select-none font-[family-name:var(--font-jetbrains-mono)] text-[9rem] font-bold leading-none text-white/[0.035] md:-top-20 md:text-[15rem]`}
      >
        {index}
      </span>
      <p className="relative mb-4 text-sm font-medium uppercase tracking-[0.25em] text-emerald-400/80 font-[family-name:var(--font-jetbrains-mono)]">
        {index} <span className="text-emerald-500/50">{"//"}</span> {code}
      </p>
      <h2 className="relative font-[family-name:var(--font-space-grotesk)] text-4xl font-bold leading-[1.02] tracking-[-0.02em] [text-wrap:balance] md:text-7xl">
        {title}
      </h2>
      {blurb && (
        <p className={`relative mt-5 max-w-2xl text-[#94A3B8] ${blurbCls}`}>{blurb}</p>
      )}
    </AnimatedSection>
  );
};

export default SectionHeader;
